import { useEffect, useRef, useState } from 'react';
import { mountGame, mountScene, type MountedScene } from '@efira/game-sdk';
import type { OverlayLayout } from '@efira/protocol';

import { FRAME_HEIGHT, FRAME_WIDTH } from '../frame';
import type { OverlayBus } from '../realtime/bus';
import { SCENE_LOADERS, isKnownScene } from './registry';

/**
 * Сколько ждать контракт игры перед монтированием.
 *
 * Монтировать игру без словаря значит показать зрителю ключи локализации
 * вместо слов. Сервер шлёт контракт немедленно при смене сцены, так что
 * ожидание измеряется миллисекундами; предел существует на случай сервера
 * старее клиента - тогда игра смонтируется с пустым контрактом и покажет хоть
 * что-то, а не ничего.
 *
 * Пока идёт ожидание, СТАРАЯ СЦЕНА ОСТАЁТСЯ НА ЭКРАНЕ: порядок «загрузить и
 * смонтировать новую, потом снять старую» этим не нарушается.
 */
const CONTRACT_DEADLINE_MS = 1500;

/**
 * Хост сцены: загружает, монтирует и заменяет.
 *
 * ТРЕБОВАНИЕ БЛОКА: страница не перезагружается, соединение не рвётся.
 * Перезагрузка browser source даёт зрителю чёрный кадр посреди эфира, а
 * переподключение сокета - ещё и паузу до восстановления конфигурации.
 *
 * ПОРЯДОК СМЕНЫ. Новая сцена сначала ЗАГРУЖАЕТСЯ, и только потом снимается
 * старая. Обратный порядок дал бы пустой кадр на всё время сетевого запроса -
 * то есть ровно тот провал, ради отсутствия которого всё это делается.
 *
 * ПЕРЕХОД. Контейнеры перекрываются, старый гаснет, новый проявляется - без
 * скачка фона и без мгновенного исчезновения. Длительность - токен
 * --motion-base; при prefers-reduced-motion он обнулён, и смена происходит
 * мгновенно, что тоже корректно.
 */
export function SceneHost({
  sceneId,
  locale,
  layout,
  bus,
}: {
  sceneId: string | null;
  locale: string;
  layout: OverlayLayout;
  bus: OverlayBus;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef<MountedScene | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  /**
   * Раскладка читается ссылкой, а не зависимостью эффекта.
   *
   * При монтировании нужна текущая, но её ПРАВКА не должна перемонтировать
   * сцену - для этого есть отдельный эффект. Без ссылки пришлось бы либо
   * перемонтировать на каждый сдвиг, либо обманывать линтер.
   */
  const layoutRef = useRef(layout);

  useEffect(() => {
    const host = hostRef.current;

    if (host === null) {
      return;
    }

    // Токен поколения: между await-ами сцена могла смениться ещё раз, и
    // применять устаревший результат нельзя - иначе при быстрых переключениях
    // на экране окажется предпоследняя сцена.
    let cancelled = false;
    setFailed(null);

    const swap = async () => {
      if (!isKnownScene(sceneId)) {
        // Неизвестный идентификатор - не повод ломаться: конфигурация могла
        // приехать от сервера новее клиента. Снимаем текущую сцену и остаёмся
        // прозрачными.
        mountedRef.current?.unmount();
        mountedRef.current = null;

        if (sceneId !== null) {
          setFailed(`неизвестная сцена: ${sceneId}`);
        }
        return;
      }

      if (mountedRef.current?.id === sceneId) {
        return;
      }

      let mounted: MountedScene;

      try {
        const loader = SCENE_LOADERS[sceneId];
        const module = await loader?.();

        if (module === undefined) {
          throw new Error(`загрузчик сцены ${sceneId} не найден`);
        }

        if (cancelled) {
          return;
        }

        const config = {
          width: FRAME_WIDTH,
          height: FRAME_HEIGHT,
          locale,
          layout: layoutRef.current,
        };

        // Игра - это сцена, дополнительно принимающая контракт и состояние.
        // Различаются они формой модуля, а не флагом в реестре: модуль либо
        // отдаёт игру, либо сцену, и третьего не бывает.
        const isGame = 'game' in module;
        const contract = isGame ? await bus.waitForContract(sceneId, CONTRACT_DEADLINE_MS) : null;

        if (cancelled) {
          return;
        }

        const container = document.createElement('div');
        container.className = 'scene-layer scene-layer--entering';
        /**
         * Какая сцена в слое - видно в разметке.
         *
         * Нужно не отладке, а воротам: смена игры устроена перекрёстным
         * затуханием, и в кадре на несколько сотен миллисекунд живут ДВЕ сцены.
         * Проверка, ищущая «элемент HUD», без этого признака находит уходящую и
         * считает, что новая уже смонтирована, - то есть шесть игр подряд
         * проверяет первую.
         */
        container.dataset['scene'] = sceneId;
        host.append(container);

        if (isGame) {
          mounted = await mountGame(module.game, {
            container,
            config,
            contract: contract ?? emptyContract(sceneId, locale),
            transport: bus,
            members: bus.members(),
          });
        } else {
          mounted = await mountScene(module.scene, container, config);
        }

        if (cancelled) {
          mounted.unmount();
          container.remove();
          return;
        }

        /**
         * Состав элементов сообщается серверу - для редактора раскладки.
         *
         * После монтирования и после первого кадра: размеры меряются по факту,
         * а до отрисовки их нет. Шина шлёт его один раз на игру - состав
         * меняется только вместе с версией.
         */
        if (isGame) {
          bus.reportLayout({ gameId: sceneId, elements: mounted.elements() });
        }

        // Новая сцена уже нарисовала первый кадр - только теперь снимаем старую.
        const previous = mountedRef.current;
        const previousContainer = previous === null ? null : host.firstElementChild;

        mountedRef.current = mounted;

        // Кроссфейд: новый слой проявляется, старый гаснет и снимается ТОЛЬКО
        // по окончании перехода. Снятый в том же кадре старый слой давал
        // провал в прозрачность на длительность перехода (design-review
        // 2026-09-17): класс --leaving ставился элементу, который тут же
        // удалялся. При prefers-reduced-motion длительность нулевая - снятие
        // происходит сразу.
        requestAnimationFrame(() => {
          container.classList.remove('scene-layer--entering');

          if (previous === null) {
            return;
          }

          previousContainer?.classList.add('scene-layer--leaving');
          const ms = parseFloat(getComputedStyle(host).getPropertyValue('--overlay-motion')) || 0;
          const finish = () => {
            previous.unmount();
            previousContainer?.remove();
          };
          if (ms <= 0) {
            finish();
          } else {
            window.setTimeout(finish, ms);
          }
        });
      } catch (error) {
        if (!cancelled) {
          // В production это не показывается зрителю: кадр остаётся
          // прозрачным, сообщение видно только с ?debug=1 (правило блока 4а).
          setFailed(String(error));
        }
      }
    };

    void swap();

    return () => {
      cancelled = true;
    };
    // Раскладка НЕ в зависимостях: её правка применяется на лету отдельным
    // эффектом ниже. Перемонтирование сцены ради сдвига на четыре пикселя
    // сбрасывало бы анимации и давало бы зрителю моргание.
  }, [sceneId, locale, bus]);

  useEffect(() => {
    layoutRef.current = layout;
    mountedRef.current?.applyLayout(layout);
  }, [layout]);

  // Размонтирование overlay целиком: освобождаем сцену, иначе её цикл кадров
  // переживёт компонент.
  useEffect(() => {
    return () => {
      mountedRef.current?.unmount();
      mountedRef.current = null;
    };
  }, []);

  return <div ref={hostRef} className="scene-host" data-failed={failed ?? undefined} />;
}

/**
 * Контракт-заглушка: игра монтируется даже без него.
 *
 * Пустой словарь означает, что `t()` вернёт ключи как есть. Это заметно и
 * некрасиво - и это правильное поведение: видимый ключ сообщает о дефекте,
 * пустой экран не сообщает ничего, и именно поэтому у конкурента
 * непереведённые подписи живут месяцами.
 */
function emptyContract(gameId: string, locale: string) {
  return {
    gameId,
    manifest: {
      id: gameId,
      version: '0.0.0',
      renderMode: 'dom',
      worldClass: 'scalar',
      bindableActions: [],
      collections: [],
      settings: { fields: [] },
      directorCommands: [],
      chatAccess: { direct: false },
      defaultBindings: [],
    },
    bindings: [],
    hudSettings: {},
    legend: [],
    legendDisplay: {
      enabled: true,
      position: 'sides',
      offsetY: -48,
      iconSize: 'm',
      perRow: 7,
      maxShown: 14,
      showLabels: true,
      showMultiplier: true,
      showCount: true,
      style: 'clean',
      greyGifts: false,
      giftImagesOnly: false,
      autoHide: false,
      animation: 'off',
    },
    frameBackground: { transparent: true, color: '#000000', image: '' },
    dictionary: {},
    locale,
  } as const satisfies Parameters<typeof mountGame>[1]['contract'];
}
